import React, { useState, useMemo, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { NHS111EmergencySupplyForm } from "./NHS111EmergencySupplyForm";
import { NHSContraceptionForm } from "./NHSContraceptionForm";
import {
  nhs111TemplateId,
  nhsSinusitisTemplateId,
  nhsUtiTemplateId,
  nhsSoreThroatTemplateId,
  nhsImpetigoTemplateId,
  nhsInsectBitesTemplateId,
  nhsShinglesTemplateId,
  nhsAomTemplateId,
  nhsHypertensionStage1TemplateId,
  nhsHypertensionStage1ServiceId,
  nhsHypertensionABPMFittingTemplateId,
  nhsHypertensionABPMFollowupTemplateId,
  nhsAhtWeightTemplateId,
  nhsAhtFollowUpTemplateId,
  nhsAhtSixMonthTemplateId,
  nhsAhtWeightServiceId,
} from "@/data/constantStrings";
import { AHTFollowUpReviewTemplate } from "./AHTFollowUpReviewTemplate";
import { AHTSixMonthReviewTemplate } from "./AHTSixMonthReviewTemplate";
import { calculateTreatmentPlanDates } from "@/lib/pharmacyFirst/utils/calculateTreatmentPlanDates";
import {
  setPatientTabSwitcher,
  setNhs111Form,
  setNhs111Prescriptions,
  setNhs111EmergencySupplyMedications,
  addupdateConsultationSummary,
  getPatientConsultationByConsultationId,
  setNhsContraceptionForm,
  addPatientVitals,
  updateConsultationActions,
  setVitalsDetails,
  setNhsSinusitisForm,
  setNhsUtiForm,
  setNhsSoreThroatForm,
  setNhsImpetigoForm,
  setNhsInsectBitesForm,
  setNhsShinglesForm,
  setNhsAomForm,
  setPendingCalculatorId,
  setHypertensionInitialForm,
  setHypertensionABPMFittingForm,
  setHypertensionABPMFollowupForm,
  parseAbpmReport,
  updateConsentObtained,
  setNhsAhtForm,
  SaveAIChatMessage,
  getPatientVitals,
  GetPatientVitalsSummaryByPatientId,
  setIncrementVitalsCompleted,
  updatePatientDetails,
  setSelectedPatientDetails,
} from "@/dispatchers/patientDispatchers";
import { useToast } from "@/hooks/use-toast";
import useTitanApp from "@/hooks/useTitanApp";
import { ActionType } from "@/enum/ActionType";
import {
  PathwayEngine,
  ConsultationSummary,
} from "@/components/pharmacy-first";
import { getPathway } from "@/lib/pharmacyFirst";
import { calculateAgeYearsFromDateOfBirth } from "@/lib/pharmacyFirst/utils/ageCalculator";
import { NHSHypertensionInitialForm } from "./NHSHypertensionInitialForm";
import {
  HYPERTENSION_STAGES,
  HYPERTENSION_SERVICE_NAME,
  getStage,
} from "@/lib/hypertensionCaseFinding";
import { NHSHypertensionABPMFittingForm } from "./NHSHypertensionABPMFittingForm";
import { NHSHypertensionABPMFollowUpForm } from "./NHSHypertensionABPMFollowUpForm";
import { resolveNextPathway } from "@/lib/pharmacyFirst/chains";
import { mapGenderToBiologicalSex } from "@/lib/pharmacyFirst/pathways/aht/weight-selection.pathway";
import { ethnicityTypes } from "@/enum/ethnicityType";
import { addScheduleEvent } from "@/dispatchers/scheduleEventsDispatchers";
import {
  addConsultationLinkedTemplate,
  getPatientActiveTreatmentPlan,
} from "@/dispatchers/treatmentPlanDispatchers";
import {
  deserializeFromTemplate,
  clearSavedPathwayState,
} from "@/lib/pharmacyFirst/saveResume";
export function NHSServicesForms({
  isDarkMode = false,
  selectedPatientConsultation,
  consultationId,
  showPreConsult,
  showNHS111ReferralTab,
  onTemplateCancel,
}) {
  const { toast } = useToast();

  const { patientState, appState } = useTitanApp();
  const { currentPharmacyId, darkMode, companyId, currentUser } =
    appState || {};

  const [
    nhsHypertensionInitialFormLoading,
    setNhsHypertensionInitialFormLoading,
  ] = useState(false);
  const {
    patientVitals,
    selectedPatientDetails,
    patientConsultations,
    patientAllergies,
  } = patientState || {};
  const [nhsContraceptionFormLoading, setNhsContraceptionFormLoading] =
    useState(false);
  const [ahtFollowUpLoading, setAhtFollowUpLoading] = useState(false);
  const [ahtSixMonthLoading, setAhtSixMonthLoading] = useState(false);
  const [nhsSinusitisFormLoading, setNhsSinusitisFormLoading] = useState(false);
  const [
    nhsHypertensionABPMFittingFormLoading,
    setNhsHypertensionABPMFittingFormLoading,
  ] = useState(false);
  const [
    nhsHypertensionABPMFollowupFormLoading,
    setNhsHypertensionABPMFollowupFormLoading,
  ] = useState(false);

  const savedSnapshot = useMemo(
    () => deserializeFromTemplate(selectedPatientConsultation?.template),
    [selectedPatientConsultation?.template],
  );

  const hasExistingTemplate = Boolean(
    selectedPatientConsultation?.template?.templateSections?.length,
  );

  const completedAudit = useMemo(() => {
    try {
      const raw = selectedPatientConsultation?.consultationSummary;
      if (!raw) return null;
      let parsed = JSON.parse(raw);
      if (typeof parsed === "string") parsed = JSON.parse(parsed);
      if (parsed?.patient && parsed?.finalOutcome) return parsed;
      return null;
    } catch {
      return null;
    }
  }, [selectedPatientConsultation?.consultationSummary]);

  const renderCompletedSummary = (pathwayId) => {
    try {
      const pathway = getPathway(pathwayId);
      return (
        <ConsultationSummary
          audit={completedAudit}
          pathway={pathway}
          isDarkMode={isDarkMode}
          readOnly
        />
      );
    } catch {
      return null;
    }
  };

  const [ahtChainPathwayId, setAhtChainPathwayId] = useState(
    () => savedSnapshot?.chain?.ahtChainPathwayId || "aht-wt-selector",
  );
  const [ahtChainContext, setAhtChainContext] = useState(
    () => savedSnapshot?.chain?.ahtChainContext || {},
  );
  const [ahtChainSections, setAhtChainSections] = useState(
    () => savedSnapshot?.chain?.ahtChainSections || [],
  );

  const hasRestoredChain = useRef(false);
  useEffect(() => {
    if (savedSnapshot?.chain && !hasRestoredChain.current) {
      hasRestoredChain.current = true;
      setAhtChainPathwayId(savedSnapshot.chain.ahtChainPathwayId);
      setAhtChainContext(savedSnapshot.chain.ahtChainContext);
      setAhtChainSections(savedSnapshot.chain.ahtChainSections);
    }
  }, [savedSnapshot]);

  const resolvedAhtPathwayId =
    !hasRestoredChain.current && savedSnapshot?.chain
      ? savedSnapshot.chain.ahtChainPathwayId
      : ahtChainPathwayId;

  const effectiveTemplateId = useMemo(() => {
    const tid = selectedPatientConsultation?.templateId;
    if (tid) return tid;
    if (!savedSnapshot) return null;
    const pid = savedSnapshot.engine.pathwayId;
    if (pid.startsWith("aht-")) return nhsAhtWeightTemplateId;
    const pathwayToTemplate = {
      sinusitis: nhsSinusitisTemplateId,
      uti: nhsUtiTemplateId,
      sorethroat: nhsSoreThroatTemplateId,
      impetigo: nhsImpetigoTemplateId,
      insectbites: nhsInsectBitesTemplateId,
      shingles: nhsShinglesTemplateId,
      otitismedia: nhsAomTemplateId,
    };
    return pathwayToTemplate[pid] || null;
  }, [selectedPatientConsultation?.templateId, savedSnapshot]);

  const parseActionTime = (action) => {
    try {
      const timeString = action.performedAt || action.actionPerformedAt;
      if (timeString) {
        return new Date(timeString).getTime();
      }
      const payload = JSON.parse(action.payload);
      if (payload.timestamp) {
        return new Date(payload.timestamp).getTime();
      }
      if (payload.vitalsDetails?.recordedAt) {
        return new Date(payload.vitalsDetails.recordedAt).getTime();
      }
    } catch (e) {}
    return new Date().getTime();
  };

  const getLatestHeightFromActions = () => {
    if (!selectedPatientConsultation?.actions) return null;

    let latestHeight = null;
    let latestTime = 0;

    for (const action of selectedPatientConsultation.actions) {
      if (action.actionName === "RecordVitals" && action.isPerformed) {
        try {
          const payload = JSON.parse(action.payload);
          const vitalType = payload?.vitalsDetails?.vitalType;
          const heightData = payload?.vitalsDetails?.height;

          if (
            (vitalType === "Height" || vitalType === "BMI Calculator") &&
            heightData
          ) {
            const actionTime = parseActionTime(action);

            if (actionTime > latestTime) {
              latestTime = actionTime;
              latestHeight = {
                value: heightData.Value,
                unit: heightData.Unit || heightData.unit,
                recordedAt: heightData.recordedAt,
              };
            }
          }
        } catch (e) {
          continue;
        }
      }
    }

    return latestHeight;
  };

  const getLatestWeightFromActions = () => {
    if (!selectedPatientConsultation?.actions) return null;

    let latestWeight = null;
    let latestTime = 0;

    for (const action of selectedPatientConsultation.actions) {
      if (action.actionName === "RecordVitals" && action.isPerformed) {
        try {
          const payload = JSON.parse(action.payload);
          const vitalType = payload?.vitalsDetails?.vitalType;
          const weightData = payload?.vitalsDetails?.weight;

          if (
            (vitalType === "Weight" || vitalType === "BMI Calculator") &&
            weightData
          ) {
            const actionTime = parseActionTime(action);

            if (actionTime > latestTime) {
              latestTime = actionTime;
              latestWeight = {
                value: weightData.Value,
                unit: weightData.Unit || weightData.unit,
                recordedAt: weightData.recordedAt,
              };
            }
          }
        } catch (e) {
          continue;
        }
      }
    }

    return latestWeight;
  };

  const hasHeightInActions = () => {
    return getLatestHeightFromActions() !== null;
  };

  const hasWeightInActions = () => {
    return getLatestWeightFromActions() !== null;
  };

  const addNewPatientVital = async (vitalType, vitalTypeKey, values) => {
    const recordedAt = new Date().toISOString();

    let obj = {
      patientId: String(selectedPatientConsultation?.patientId || ""),
      patientName: String(selectedPatientConsultation?.patientName || ""),
      consultationId: consultationId,
      recordedAt: recordedAt,
      vitalType: vitalType,
      vitalTypeKey: vitalTypeKey,
      serviceName: selectedPatientConsultation?.serviceName,
    };

    let heightAdded = null;
    let weightAdded = null;
    let shouldCreateOrUpdateBMI = false;

    // ✅ WEIGHT
    if (vitalType === "Weight") {
      weightAdded = {
        value: String(values?.Value || ""),
        unit: String(values?.Unit || ""),
      };

      obj.weight = {
        Value: weightAdded.value,
        Unit: weightAdded.unit,
        recordedAt: recordedAt,
      };

      const heightExists =
        hasHeightInActions() || !!patientVitals?.height?.value;

      if (heightExists) {
        shouldCreateOrUpdateBMI = true;
      }
    }

    // ✅ BLOOD PRESSURE
    else if (vitalType === "Blood Pressure") {
      obj.bloodPressure = {
        Systolic: {
          Value: String(values?.Systolic || ""),
          Unit: "mmHg",
          recordedAt: recordedAt,
        },
        Diastolic: {
          Value: String(values?.Diastolic || ""),
          Unit: "mmHg",
          recordedAt: recordedAt,
        },
        recordedAt: recordedAt,
      };
    }

    // ✅ HEIGHT
    else if (vitalType === "Height") {
      let heightString = "";

      if (values?.Unit === "cm") {
        heightString = String(values?.Value || "");
      } else if (values?.Unit === "ft/in") {
        const feet = parseInt(values?.Value?.Feet) || 0;
        const inch =
          values?.Value?.Inch === "" || values?.Value?.Inch == null
            ? 0
            : parseInt(values?.Value?.Inch);

        heightString = `${feet} ft ${inch} in`;
      }

      heightAdded = {
        value: heightString,
        unit: String(values?.Unit || ""),
      };

      obj.height = {
        Value: heightAdded.value,
        Unit: heightAdded.unit,
        recordedAt: recordedAt,
      };

      const weightExists =
        hasWeightInActions() || !!patientVitals?.weight?.value;

      if (weightExists) {
        shouldCreateOrUpdateBMI = true;
      }
    }

    // ✅ Payload
    const vitalsPayload = {
      payload: JSON.stringify({
        vitalsDetails: obj,
        timestamp: recordedAt,
        actionPerformedAt: recordedAt,
      }),
      actionName: ActionType[5],
      actionNumber: "5",
      isPerformed: true,
      performedAt: recordedAt,
    };

    try {
      const response = await addPatientVitals(obj);

      if (consultationId) {
        await updateConsultationActions({
          consultationId: consultationId,
          payload: vitalsPayload,
        });
        await getPatientConsultationByConsultationId(consultationId);
        await SaveAIChatMessage({
          ConsultationChat: JSON.stringify([
            {
              sender: "success",
              message: "Vitals completed successfully.",
              actionId: 5,
              details: vitalsPayload,
            },
          ]),
          ConsultationId: consultationId,
        });
      }

      setVitalsDetails(vitalsPayload);
      setIncrementVitalsCompleted(true);
      await getPatientVitals(selectedPatientDetails?.patientID);
      await GetPatientVitalsSummaryByPatientId(
        selectedPatientDetails?.patientID,
      );
    } catch (error) {
      console.error("Error in handleAddClick:", error);
    }
  };

  const handlePathwayVitalSave = async (vitalData) => {
    if (!vitalData?.type) return;
    switch (vitalData.type) {
      case "weight":
        await addNewPatientVital("Weight", "weight", {
          Value: vitalData.data.value,
          Unit: vitalData.data.unit || "kg",
        });
        break;
      case "height":
        await addNewPatientVital("Height", "height", {
          Value: vitalData.data.value,
          Unit: vitalData.data.unit || "cm",
        });
        break;
      case "bmi": {
        const bmiVal = vitalData.data.value;
        const recordedAt = new Date().toISOString();
        const baseVital = {
          patientId: String(selectedPatientConsultation?.patientId || ""),
          patientName: String(selectedPatientConsultation?.patientName || ""),
          consultationId: consultationId,
          recordedAt,
          serviceName: selectedPatientConsultation?.serviceName,
        };

        try {
          if (vitalData.data.heightCm) {
            await addPatientVitals({
              ...baseVital,
              vitalType: "Height",
              vitalTypeKey: "height",
              height: {
                Value: String(vitalData.data.heightCm),
                Unit: "cm",
                recordedAt,
              },
            });
          }
          if (vitalData.data.weightKg) {
            await addPatientVitals({
              ...baseVital,
              vitalType: "Weight",
              vitalTypeKey: "weight",
              weight: {
                Value: String(vitalData.data.weightKg),
                Unit: "kg",
                recordedAt,
              },
            });
          }

          const bmiObj = {
            ...baseVital,
            vitalType: "BMI",
            vitalTypeKey: "bmi",
            bmi: { Value: bmiVal, Unit: "kg/m²", recordedAt },
            recordedVitalType: "bmi",
          };
          if (vitalData.data.heightCm) {
            bmiObj.height = {
              Value: String(vitalData.data.heightCm),
              Unit: "cm",
              recordedAt,
            };
          }
          if (vitalData.data.weightKg) {
            bmiObj.weight = {
              Value: String(vitalData.data.weightKg),
              Unit: "kg",
              recordedAt,
            };
          }
          const bmiPayload = {
            payload: JSON.stringify({
              vitalsDetails: bmiObj,
              timestamp: recordedAt,
              actionPerformedAt: recordedAt,
            }),
            actionName: ActionType[5],
            actionNumber: "5",
            isPerformed: true,
            performedAt: recordedAt,
          };
          await addPatientVitals(bmiObj);
          if (consultationId) {
            await updateConsultationActions({
              consultationId,
              payload: bmiPayload,
            });
            await getPatientConsultationByConsultationId(consultationId);
            await SaveAIChatMessage({
              ConsultationChat: JSON.stringify([
                {
                  sender: "success",
                  message: "Vitals completed successfully.",
                  actionId: 5,
                  details: bmiPayload,
                },
              ]),
              ConsultationId: consultationId,
            });
          }
          setVitalsDetails(bmiPayload);
          setIncrementVitalsCompleted(true);
          await getPatientVitals(selectedPatientDetails?.patientID);
          await GetPatientVitalsSummaryByPatientId(
            selectedPatientDetails?.patientID,
          );
        } catch (error) {
          console.error("Error saving BMI vital:", error);
        }
        break;
      }
      case "blood-pressure":
        await addNewPatientVital("Blood Pressure", "bloodpressure", {
          Systolic: Number(vitalData.data.systolic),
          Diastolic: Number(vitalData.data.diastolic),
        });
        break;
      case "temperature":
        console.warn(
          "Temperature vital save from pathway not yet wired to addNewPatientVital",
        );
        break;
      default:
        break;
    }
  };

  const toNum = (v) => (v === "" ? null : Number(v));
  const calcAvgBp = (data) => {
    const sys = [data.bp2Systolic, data.bp3Systolic]
      .map(toNum)
      .filter((n) => n !== null && !Number.isNaN(n));

    const dia = [data.bp2Diastolic, data.bp3Diastolic]
      .map(toNum)
      .filter((n) => n !== null && !Number.isNaN(n));

    if (sys.length === 0 || dia.length === 0) return null;

    return {
      systolic: Math.round(sys.reduce((a, b) => a + b, 0) / sys.length),
      diastolic: Math.round(dia.reduce((a, b) => a + b, 0) / dia.length),
    };
  };

  const buildPatientInput = () => {
    const dob = selectedPatientDetails?.dob
      ? new Date(selectedPatientDetails.dob).toISOString().split("T")[0]
      : undefined;
    const ageYears = dob
      ? (calculateAgeYearsFromDateOfBirth(dob) ?? undefined)
      : undefined;
    const sexNum = selectedPatientDetails?.sex;
    const isFemale = sexNum === 2 ? true : sexNum === 1 ? false : undefined;
    const genderStr =
      sexNum === 1 ? "male" : sexNum === 2 ? "female" : undefined;
    const ethnicityLabel = ethnicityTypes[selectedPatientDetails?.ethnicity];
    return {
      patientId:
        selectedPatientDetails?.patientID ||
        selectedPatientConsultation?.patientId ||
        "current-patient",
      nhsNumber: selectedPatientDetails?.nhsNumber || undefined,
      ageYears,
      dateOfBirth: dob || "",
      isFemale,
      biologicalSex: mapGenderToBiologicalSex(genderStr),
      ethnicAdjustmentGroup:
        ethnicityLabel &&
        ethnicityLabel !== "Not Specified" &&
        ethnicityLabel !== "Not Known"
          ? ethnicityLabel
          : undefined,
      weightKg: selectedPatientDetails?.weightKg || undefined,
      knownAllergies: (patientAllergies?.length > 0
        ? patientAllergies
        : selectedPatientDetails?.allergies || []
      ).map((a) => {
        const severityMap = { 1: "mild", 2: "moderate", 3: "severe" };
        if (a?.allergyName) {
          return {
            substance: a.allergyName,
            severity:
              typeof a.severity === "number"
                ? severityMap[a.severity]
                : a.severity,
          };
        }
        return {
          substance:
            typeof a === "string" ? a : a?.name || a?.substance || "Unknown",
          severity:
            typeof a?.severity === "number"
              ? severityMap[a.severity]
              : a?.severity,
        };
      }),
      currentMedications: (selectedPatientDetails?.medications || []).map(
        (m) =>
          typeof m === "string" ? m : m?.name || m?.medication || "Unknown",
      ),
    };
  };

  const getAHTTreatmentPlan = () => {
    if (!patientConsultations) return null;
    for (const consult of patientConsultations) {
      if (!consult?.actions) continue;
      for (const action of consult.actions) {
        if (action.actionName === "CreateTreatmentPlan" && action.isPerformed) {
          try {
            const payload =
              typeof action.payload === "string"
                ? JSON.parse(action.payload)
                : action.payload;
            if (payload?.resolvedDrugId) return payload;
          } catch {
            continue;
          }
        }
      }
    }
    return null;
  };

  const handleAhtFollowUpComplete = async (data) => {
    setAhtFollowUpLoading(true);
    const plan = getAHTTreatmentPlan();

    const jsonData = JSON.stringify({
      formType: "aht-follow-up-4wk",
      ...data,
      treatmentPlanId: plan?.id,
      resolvedDrugId: plan?.resolvedDrugId,
    });

    await addupdateConsultationSummary({ consultationId, summary: jsonData });

    if (plan && data.decision !== "Cease" && data.decision !== "Hold") {
      const newWeek = (plan.currentWeek || 1) + 4;
      const reviewSchedule = plan.reviewSchedule;
      let updatedDates = null;
      if (reviewSchedule) {
        updatedDates = calculateTreatmentPlanDates({
          actualSupplyDate: new Date().toISOString().split("T")[0],
          initialSupplyDate: plan.initialSupplyDate,
          currentWeek: newWeek,
          reviewSchedule,
        });
      }

      const supplyEvent = {
        id: uuidv4(),
        consultationId,
        supplyDate: new Date().toISOString().split("T")[0],
        drugId: plan.resolvedDrugId,
        dose: data.medicinePrescribed || plan.currentDose,
        batchNumber: data.supplyBatch || data.batchNo,
        expiryDate: data.supplyExpiry || "",
        brandName: data.brandName || null,
        weightKg: parseFloat(data.weightKg) || plan.baselineWeightKg,
        weekNumber: newWeek,
        wasInTitrationPhase: updatedDates?.isInTitrationPhase ?? false,
        pharmacistName: data.sigName,
        pharmacistRegistrationNumber: data.sigRegNo,
        clinicalRationale: data.clinicalNotes,
        prescriberDecision:
          data.decision === "Escalate to next dose" ? "escalate" : "continue",
      };

      const updatedPlan = {
        ...plan,
        currentWeek: newWeek,
        currentDose: data.medicinePrescribed || plan.currentDose,
        ...(updatedDates && {
          nextQuestionnaireDueDate: updatedDates.nextQuestionnaireDueDate,
          nextSupplyDueDate: updatedDates.nextSupplyDueDate,
        }),
        supplyHistory: [...(plan.supplyHistory || []), supplyEvent],
        updatedAt: new Date().toISOString(),
      };

      const actionPayload = {
        payload: JSON.stringify(updatedPlan),
        actionName: "CreateTreatmentPlan",
        actionNumber: "15",
        isPerformed: true,
      };
      await updateConsultationActions({
        consultationId: plan.parentConsultationId || consultationId,
        payload: actionPayload,
      });
    }

    await getPatientConsultationByConsultationId(consultationId);
    setPatientTabSwitcher(showPreConsult || showNHS111ReferralTab ? 2 : 1);
    toast({
      title: "4-Week Review Completed",
      description:
        "AHT Weight Management follow-up review has been completed successfully.",
    });
    setAhtFollowUpLoading(false);
  };

  const handleAhtSixMonthComplete = async (data) => {
    setAhtSixMonthLoading(true);
    const plan = getAHTTreatmentPlan();

    const jsonData = JSON.stringify({
      formType: "aht-six-month-review",
      ...data,
      treatmentPlanId: plan?.id,
      resolvedDrugId: plan?.resolvedDrugId,
    });

    await addupdateConsultationSummary({ consultationId, summary: jsonData });

    if (plan) {
      const updatedPlan = {
        ...plan,
        lastSectionBReviewDate: new Date().toISOString().split("T")[0],
        lastSectionBDecision: data.decision,
        updatedAt: new Date().toISOString(),
      };

      if (data.nextSixMonthDate) {
        updatedPlan.nextSectionBReviewDueDate = data.nextSixMonthDate;
      }

      const actionPayload = {
        payload: JSON.stringify(updatedPlan),
        actionName: "CreateTreatmentPlan",
        actionNumber: "15",
        isPerformed: true,
      };
      await updateConsultationActions({
        consultationId: plan.parentConsultationId || consultationId,
        payload: actionPayload,
      });
    }

    await getPatientConsultationByConsultationId(consultationId);
    setPatientTabSwitcher(showPreConsult || showNHS111ReferralTab ? 2 : 1);
    toast({
      title: "6-Month Review Completed",
      description:
        "AHT Weight Management 6-month independent review has been completed successfully.",
    });
    setAhtSixMonthLoading(false);
  };

  const handleAhtComplete = async (audit) => {
    const outcome = audit.finalOutcome;

    if (outcome?.outcomeType === "chain" && outcome?.chainId) {
      const prevStartedAt = ahtChainContext._chainStartedAt || audit.startedAt;
      const prevAnswerCount =
        (ahtChainContext._chainAnswerCount || 0) + (audit.answers?.length || 0);
      const visibleCount =
        audit.contextSnapshot?._visibleAnswerCount ??
        audit.answers?.length ??
        0;
      const prevVisibleCount =
        (ahtChainContext._chainVisibleAnswerCount || 0) + visibleCount;
      const prevChainAuditAnswers = ahtChainContext._chainAuditAnswers || [];
      const mergedContext = {
        ...ahtChainContext,
        ...(audit.contextSnapshot ?? {}),
        _chainStartedAt: prevStartedAt,
        _chainAnswerCount: prevAnswerCount,
        _chainVisibleAnswerCount: prevVisibleCount,
        _chainAuditAnswers: [
          ...prevChainAuditAnswers,
          ...(audit.answers || []),
        ],
      };
      setAhtChainContext(mergedContext);

      setAhtChainSections((prev) => [
        ...prev,
        {
          pathwayId: ahtChainPathwayId,
          label: audit.pathwayName ?? ahtChainPathwayId,
          completedAt: new Date().toISOString(),
        },
      ]);

      const nextPathwayId = resolveNextPathway(
        outcome.chainId,
        mergedContext,
        audit,
      );
      if (nextPathwayId) {
        setAhtChainPathwayId(nextPathwayId);
        return;
      }
    }

    const chainStartedAt = ahtChainContext._chainStartedAt || audit.startedAt;
    const currentVisibleCount =
      audit.contextSnapshot?._visibleAnswerCount ?? audit.answers?.length ?? 0;
    const chainVisibleCount =
      (ahtChainContext._chainVisibleAnswerCount || 0) + currentVisibleCount;
    const enrichedAudit = {
      ...audit,
      _chainStartedAt: chainStartedAt,
      _chainVisibleAnswerCount: chainVisibleCount,
    };

    setNhsAhtForm(enrichedAudit);
    await updateConsentObtained({ consultationId, consentObtained: true });
    await addupdateConsultationSummary({
      consultationId,
      summary: JSON.stringify({
        ...enrichedAudit,
        chainSections: ahtChainSections,
      }),
    });
    await clearSavedPathwayState(consultationId);
    await getPatientConsultationByConsultationId(consultationId);
    setPatientTabSwitcher(showPreConsult || showNHS111ReferralTab ? 2 : 1);
    toast({
      title: "Consultation Completed",
      description:
        "AHT Weight Management consultation has been completed successfully.",
    });

    setAhtChainPathwayId("aht-wt-selector");
    setAhtChainContext({});
    setAhtChainSections([]);
  };

  const handleDemographicAmended = async (amendments) => {
    if (!amendments?.length || !selectedPatientDetails) return;

    const ethnicityLabelToKey = {};
    for (const [key, label] of Object.entries(ethnicityTypes)) {
      ethnicityLabelToKey[label] = parseInt(key);
    }

    const updates = {};
    for (const amendment of amendments) {
      switch (amendment.field) {
        case "dateOfBirth": {
          const val = amendment.newValue;
          if (val) {
            const parsed = new Date(val);
            if (!isNaN(parsed.getTime())) {
              updates.dob = parsed.toISOString();
            }
          }
          break;
        }
        case "biologicalSex":
          if (amendment.newValue === "male") updates.sex = 1;
          else if (amendment.newValue === "female") updates.sex = 2;
          break;
        case "isFemale":
          if (amendment.newValue === "true" || amendment.newValue === true)
            updates.sex = 2;
          else if (
            amendment.newValue === "false" ||
            amendment.newValue === false
          )
            updates.sex = 1;
          break;
        case "ethnicAdjustmentGroup": {
          const numKey = ethnicityLabelToKey[amendment.newValue];
          if (numKey !== undefined) updates.ethnicity = numKey;
          break;
        }
        default:
          break;
      }
    }

    if (Object.keys(updates).length === 0) return;

    const { patientName, ...detailsWithoutName } = selectedPatientDetails;
    const data = { ...detailsWithoutName, ...updates };

    try {
      const response = await updatePatientDetails(data);
      if (response === "true") {
        setSelectedPatientDetails({ ...selectedPatientDetails, ...updates });
      }
    } catch (err) {
      console.error("Failed to update patient demographics:", err);
    }
  };

  return effectiveTemplateId === nhs111TemplateId ? (
    <NHS111EmergencySupplyForm
      isDarkMode={isDarkMode}
      fromKanbanOrList={false}
      onComplete={async (data, prescriptions, emergencySupplyMedications) => {
        const summaryPayload = {
          data,
          emergencySupplyMedications,
        };

        const jsonData = JSON.stringify(summaryPayload);

        await addupdateConsultationSummary({
          consultationId,
          summary: jsonData,
        });

        await getPatientConsultationByConsultationId(consultationId);

        setNhs111Form(data);
        setNhs111Prescriptions(prescriptions);
        setNhs111EmergencySupplyMedications(emergencySupplyMedications || []);

        setPatientTabSwitcher(showPreConsult || showNHS111ReferralTab ? 2 : 1);

        toast({
          title: "Consultation Completed",
          description:
            "NHS 111 Emergency Supply consultation has been completed successfully.",
        });
      }}
      consultationStatus={selectedPatientConsultation?.consultationStatus}
    />
  ) : effectiveTemplateId === nhsSinusitisTemplateId ? (
    completedAudit?.pathwayId === "sinusitis" ? (
      renderCompletedSummary("sinusitis")
    ) : (
      <PathwayEngine
        key={`${consultationId}-sinusitis`}
        pathwayId="sinusitis"
        patient={(() => {
          const dob = selectedPatientDetails?.dob
            ? new Date(selectedPatientDetails.dob).toISOString().split("T")[0]
            : undefined;
          const ageYears = dob
            ? (calculateAgeYearsFromDateOfBirth(dob) ?? undefined)
            : undefined;
          const sexNum = selectedPatientDetails?.sex;
          const isFemale =
            sexNum === 2 ? true : sexNum === 1 ? false : undefined;
          return {
            patientId:
              selectedPatientDetails?.patientID ||
              selectedPatientConsultation?.patientId ||
              "current-patient",
            ageYears,
            dateOfBirth: dob || "",
            isFemale,
            weightKg: selectedPatientDetails?.weightKg || undefined,
            knownAllergies: (selectedPatientDetails?.allergies || []).map(
              (a) => ({
                substance:
                  typeof a === "string"
                    ? a
                    : a?.allergyName || a?.name || a?.substance || "Unknown",
                severity: a?.severity,
              }),
            ),
            currentMedications: (selectedPatientDetails?.medications || []).map(
              (m) =>
                typeof m === "string"
                  ? m
                  : m?.name || m?.medication || "Unknown",
            ),
          };
        })()}
        isDarkMode={isDarkMode}
        consultationId={consultationId}
        companyId={companyId}
        templateName="Pharmacy First - Sinusitis"
        hasExistingTemplate={hasExistingTemplate}
        templateId={effectiveTemplateId}
        restoredState={
          savedSnapshot?.engine?.pathwayId === "sinusitis"
            ? savedSnapshot.engine
            : undefined
        }
        onComplete={async (audit) => {
          setNhsSinusitisForm(audit);
          await updateConsentObtained({
            consultationId,
            consentObtained: true,
          });
          await addupdateConsultationSummary({
            consultationId,
            summary: JSON.stringify(audit),
          });
          await clearSavedPathwayState(consultationId);
          await getPatientConsultationByConsultationId(consultationId);
          setPatientTabSwitcher(
            showPreConsult || showNHS111ReferralTab ? 2 : 1,
          );
          toast({
            title: "Consultation Completed",
            description:
              "NHS Pharmacy First — Acute Sinusitis consultation has been completed successfully.",
          });
        }}
        onOpenCalculator={(calculatorId) =>
          setPendingCalculatorId(calculatorId)
        }
        onSaveVitals={handlePathwayVitalSave}
        onDemographicAmended={handleDemographicAmended}
        onConsentRefused={() => {
          updateConsentObtained({ consultationId, consentObtained: false });
        }}
        onCancel={() => {
          setNhsSinusitisForm(null);
          onTemplateCancel?.();
        }}
      />
    )
  ) : effectiveTemplateId === nhsUtiTemplateId ? (
    completedAudit?.pathwayId === "uti" ? (
      renderCompletedSummary("uti")
    ) : (
      <PathwayEngine
        key={`${consultationId}-uti`}
        pathwayId="uti"
        patient={(() => {
          const dob = selectedPatientDetails?.dob
            ? new Date(selectedPatientDetails.dob).toISOString().split("T")[0]
            : undefined;
          const ageYears = dob
            ? (calculateAgeYearsFromDateOfBirth(dob) ?? undefined)
            : undefined;
          const sexNum = selectedPatientDetails?.sex;
          const isFemale =
            sexNum === 2 ? true : sexNum === 1 ? false : undefined;
          return {
            patientId:
              selectedPatientDetails?.patientID ||
              selectedPatientConsultation?.patientId ||
              "current-patient",
            ageYears,
            dateOfBirth: dob || "",
            isFemale,
            weightKg: selectedPatientDetails?.weightKg || undefined,
            knownAllergies: (selectedPatientDetails?.allergies || []).map(
              (a) => ({
                substance:
                  typeof a === "string"
                    ? a
                    : a?.allergyName || a?.name || a?.substance || "Unknown",
                severity: a?.severity,
              }),
            ),
            currentMedications: (selectedPatientDetails?.medications || []).map(
              (m) =>
                typeof m === "string"
                  ? m
                  : m?.name || m?.medication || "Unknown",
            ),
          };
        })()}
        isDarkMode={isDarkMode}
        consultationId={consultationId}
        companyId={companyId}
        templateName="Pharmacy First - UTI"
        hasExistingTemplate={hasExistingTemplate}
        templateId={effectiveTemplateId}
        restoredState={
          savedSnapshot?.engine?.pathwayId === "uti"
            ? savedSnapshot.engine
            : undefined
        }
        onComplete={async (audit) => {
          setNhsUtiForm(audit);
          await updateConsentObtained({
            consultationId,
            consentObtained: true,
          });
          await addupdateConsultationSummary({
            consultationId,
            summary: JSON.stringify(audit),
          });
          await clearSavedPathwayState(consultationId);
          await getPatientConsultationByConsultationId(consultationId);
          setPatientTabSwitcher(
            showPreConsult || showNHS111ReferralTab ? 2 : 1,
          );
          toast({
            title: "Consultation Completed",
            description:
              "NHS Pharmacy First — Uncomplicated UTI consultation has been completed successfully.",
          });
        }}
        onOpenCalculator={(calculatorId) =>
          setPendingCalculatorId(calculatorId)
        }
        onSaveVitals={handlePathwayVitalSave}
        onDemographicAmended={handleDemographicAmended}
        onConsentRefused={() => {
          updateConsentObtained({ consultationId, consentObtained: false });
        }}
        onCancel={() => {
          setNhsUtiForm(null);
          onTemplateCancel?.();
        }}
      />
    )
  ) : effectiveTemplateId === nhsSoreThroatTemplateId ? (
    completedAudit?.pathwayId === "sorethroat" ? (
      renderCompletedSummary("sorethroat")
    ) : (
      <PathwayEngine
        key={`${consultationId}-sorethroat`}
        pathwayId="sorethroat"
        patient={(() => {
          const dob = selectedPatientDetails?.dob
            ? new Date(selectedPatientDetails.dob).toISOString().split("T")[0]
            : undefined;
          const ageYears = dob
            ? (calculateAgeYearsFromDateOfBirth(dob) ?? undefined)
            : undefined;
          const sexNum = selectedPatientDetails?.sex;
          const isFemale =
            sexNum === 2 ? true : sexNum === 1 ? false : undefined;
          return {
            patientId:
              selectedPatientDetails?.patientID ||
              selectedPatientConsultation?.patientId ||
              "current-patient",
            ageYears,
            dateOfBirth: dob || "",
            isFemale,
            weightKg: selectedPatientDetails?.weightKg || undefined,
            knownAllergies: (selectedPatientDetails?.allergies || []).map(
              (a) => ({
                substance:
                  typeof a === "string"
                    ? a
                    : a?.allergyName || a?.name || a?.substance || "Unknown",
                severity: a?.severity,
              }),
            ),
            currentMedications: (selectedPatientDetails?.medications || []).map(
              (m) =>
                typeof m === "string"
                  ? m
                  : m?.name || m?.medication || "Unknown",
            ),
          };
        })()}
        isDarkMode={isDarkMode}
        consultationId={consultationId}
        companyId={companyId}
        templateName="Pharmacy First - Sore Throat"
        hasExistingTemplate={hasExistingTemplate}
        templateId={effectiveTemplateId}
        restoredState={
          savedSnapshot?.engine?.pathwayId === "sorethroat"
            ? savedSnapshot.engine
            : undefined
        }
        onComplete={async (audit) => {
          setNhsSoreThroatForm(audit);
          await updateConsentObtained({
            consultationId,
            consentObtained: true,
          });
          await addupdateConsultationSummary({
            consultationId,
            summary: JSON.stringify(audit),
          });
          await clearSavedPathwayState(consultationId);
          await getPatientConsultationByConsultationId(consultationId);
          setPatientTabSwitcher(
            showPreConsult || showNHS111ReferralTab ? 2 : 1,
          );
          toast({
            title: "Consultation Completed",
            description:
              "NHS Pharmacy First — Acute Sore Throat consultation has been completed successfully.",
          });
        }}
        onOpenCalculator={(calculatorId) =>
          setPendingCalculatorId(calculatorId)
        }
        onSaveVitals={handlePathwayVitalSave}
        onDemographicAmended={handleDemographicAmended}
        onConsentRefused={() => {
          updateConsentObtained({ consultationId, consentObtained: false });
        }}
        onCancel={() => {
          setNhsSoreThroatForm(null);
          onTemplateCancel?.();
        }}
      />
    )
  ) : effectiveTemplateId === nhsImpetigoTemplateId ? (
    completedAudit?.pathwayId === "impetigo" ? (
      renderCompletedSummary("impetigo")
    ) : (
      <PathwayEngine
        key={`${consultationId}-impetigo`}
        pathwayId="impetigo"
        patient={(() => {
          const dob = selectedPatientDetails?.dob
            ? new Date(selectedPatientDetails.dob).toISOString().split("T")[0]
            : undefined;
          const ageYears = dob
            ? (calculateAgeYearsFromDateOfBirth(dob) ?? undefined)
            : undefined;
          const sexNum = selectedPatientDetails?.sex;
          const isFemale =
            sexNum === 2 ? true : sexNum === 1 ? false : undefined;
          return {
            patientId:
              selectedPatientDetails?.patientID ||
              selectedPatientConsultation?.patientId ||
              "current-patient",
            ageYears,
            dateOfBirth: dob || "",
            isFemale,
            weightKg: selectedPatientDetails?.weightKg || undefined,
            knownAllergies: (selectedPatientDetails?.allergies || []).map(
              (a) => ({
                substance:
                  typeof a === "string"
                    ? a
                    : a?.allergyName || a?.name || a?.substance || "Unknown",
                severity: a?.severity,
              }),
            ),
            currentMedications: (selectedPatientDetails?.medications || []).map(
              (m) =>
                typeof m === "string"
                  ? m
                  : m?.name || m?.medication || "Unknown",
            ),
          };
        })()}
        isDarkMode={isDarkMode}
        consultationId={consultationId}
        companyId={companyId}
        templateName="Pharmacy First - Impetigo"
        hasExistingTemplate={hasExistingTemplate}
        templateId={effectiveTemplateId}
        restoredState={
          savedSnapshot?.engine?.pathwayId === "impetigo"
            ? savedSnapshot.engine
            : undefined
        }
        onComplete={async (audit) => {
          setNhsImpetigoForm(audit);
          await updateConsentObtained({
            consultationId,
            consentObtained: true,
          });
          await addupdateConsultationSummary({
            consultationId,
            summary: JSON.stringify(audit),
          });
          await clearSavedPathwayState(consultationId);
          await getPatientConsultationByConsultationId(consultationId);
          setPatientTabSwitcher(
            showPreConsult || showNHS111ReferralTab ? 2 : 1,
          );
          toast({
            title: "Consultation Completed",
            description:
              "NHS Pharmacy First — Impetigo consultation has been completed successfully.",
          });
        }}
        onOpenCalculator={(calculatorId) =>
          setPendingCalculatorId(calculatorId)
        }
        onSaveVitals={handlePathwayVitalSave}
        onDemographicAmended={handleDemographicAmended}
        onConsentRefused={() => {
          updateConsentObtained({ consultationId, consentObtained: false });
        }}
        onCancel={() => {
          setNhsImpetigoForm(null);
          onTemplateCancel?.();
        }}
      />
    )
  ) : effectiveTemplateId === nhsInsectBitesTemplateId ? (
    completedAudit?.pathwayId === "insectbites" ? (
      renderCompletedSummary("insectbites")
    ) : (
      <PathwayEngine
        key={`${consultationId}-insectbites`}
        pathwayId="insectbites"
        patient={(() => {
          const dob = selectedPatientDetails?.dob
            ? new Date(selectedPatientDetails.dob).toISOString().split("T")[0]
            : undefined;
          const ageYears = dob
            ? (calculateAgeYearsFromDateOfBirth(dob) ?? undefined)
            : undefined;
          const sexNum = selectedPatientDetails?.sex;
          const isFemale =
            sexNum === 2 ? true : sexNum === 1 ? false : undefined;
          return {
            patientId:
              selectedPatientDetails?.patientID ||
              selectedPatientConsultation?.patientId ||
              "current-patient",
            ageYears,
            dateOfBirth: dob || "",
            isFemale,
            weightKg: selectedPatientDetails?.weightKg || undefined,
            knownAllergies: (selectedPatientDetails?.allergies || []).map(
              (a) => ({
                substance:
                  typeof a === "string"
                    ? a
                    : a?.allergyName || a?.name || a?.substance || "Unknown",
                severity: a?.severity,
              }),
            ),
            currentMedications: (selectedPatientDetails?.medications || []).map(
              (m) =>
                typeof m === "string"
                  ? m
                  : m?.name || m?.medication || "Unknown",
            ),
          };
        })()}
        isDarkMode={isDarkMode}
        consultationId={consultationId}
        companyId={companyId}
        templateName="Pharmacy First - Insect Bites"
        hasExistingTemplate={hasExistingTemplate}
        templateId={effectiveTemplateId}
        restoredState={
          savedSnapshot?.engine?.pathwayId === "insectbites"
            ? savedSnapshot.engine
            : undefined
        }
        onComplete={async (audit) => {
          setNhsInsectBitesForm(audit);
          await updateConsentObtained({
            consultationId,
            consentObtained: true,
          });
          await addupdateConsultationSummary({
            consultationId,
            summary: JSON.stringify(audit),
          });
          await clearSavedPathwayState(consultationId);
          await getPatientConsultationByConsultationId(consultationId);
          setPatientTabSwitcher(showPreConsult ? 2 : 1);
          toast({
            title: "Consultation Completed",
            description:
              "NHS Pharmacy First — Infected Insect Bites consultation has been completed successfully.",
          });
        }}
        onOpenCalculator={(calculatorId) =>
          setPendingCalculatorId(calculatorId)
        }
        onSaveVitals={handlePathwayVitalSave}
        onDemographicAmended={handleDemographicAmended}
        onConsentRefused={() => {
          updateConsentObtained({ consultationId, consentObtained: false });
        }}
        onCancel={() => {
          setNhsInsectBitesForm(null);
          onTemplateCancel?.();
        }}
      />
    )
  ) : effectiveTemplateId === nhsShinglesTemplateId ? (
    completedAudit?.pathwayId === "shingles" ? (
      renderCompletedSummary("shingles")
    ) : (
      <PathwayEngine
        key={`${consultationId}-shingles`}
        pathwayId="shingles"
        patient={(() => {
          const dob = selectedPatientDetails?.dob
            ? new Date(selectedPatientDetails.dob).toISOString().split("T")[0]
            : undefined;
          const ageYears = dob
            ? (calculateAgeYearsFromDateOfBirth(dob) ?? undefined)
            : undefined;
          const sexNum = selectedPatientDetails?.sex;
          const isFemale =
            sexNum === 2 ? true : sexNum === 1 ? false : undefined;
          return {
            patientId:
              selectedPatientDetails?.patientID ||
              selectedPatientConsultation?.patientId ||
              "current-patient",
            ageYears,
            dateOfBirth: dob || "",
            isFemale,
            weightKg: selectedPatientDetails?.weightKg || undefined,
            knownAllergies: (selectedPatientDetails?.allergies || []).map(
              (a) => ({
                substance:
                  typeof a === "string"
                    ? a
                    : a?.allergyName || a?.name || a?.substance || "Unknown",
                severity: a?.severity,
              }),
            ),
            currentMedications: (selectedPatientDetails?.medications || []).map(
              (m) =>
                typeof m === "string"
                  ? m
                  : m?.name || m?.medication || "Unknown",
            ),
          };
        })()}
        isDarkMode={isDarkMode}
        consultationId={consultationId}
        companyId={companyId}
        templateName="Pharmacy First - Shingles"
        hasExistingTemplate={hasExistingTemplate}
        templateId={effectiveTemplateId}
        restoredState={
          savedSnapshot?.engine?.pathwayId === "shingles"
            ? savedSnapshot.engine
            : undefined
        }
        onComplete={async (audit) => {
          setNhsShinglesForm(audit);
          await updateConsentObtained({
            consultationId,
            consentObtained: true,
          });
          await addupdateConsultationSummary({
            consultationId,
            summary: JSON.stringify(audit),
          });
          await clearSavedPathwayState(consultationId);
          await getPatientConsultationByConsultationId(consultationId);
          setPatientTabSwitcher(showPreConsult ? 2 : 1);
          toast({
            title: "Consultation Completed",
            description:
              "NHS Pharmacy First — Shingles consultation has been completed successfully.",
          });
        }}
        onOpenCalculator={(calculatorId) =>
          setPendingCalculatorId(calculatorId)
        }
        onSaveVitals={handlePathwayVitalSave}
        onDemographicAmended={handleDemographicAmended}
        onConsentRefused={() => {
          updateConsentObtained({ consultationId, consentObtained: false });
        }}
        onCancel={() => {
          setNhsShinglesForm(null);
          onTemplateCancel?.();
        }}
      />
    )
  ) : effectiveTemplateId === nhsAomTemplateId ? (
    completedAudit?.pathwayId === "otitismedia" ? (
      renderCompletedSummary("otitismedia")
    ) : (
      <PathwayEngine
        key={`${consultationId}-otitismedia`}
        pathwayId="otitismedia"
        patient={(() => {
          const dob = selectedPatientDetails?.dob
            ? new Date(selectedPatientDetails.dob).toISOString().split("T")[0]
            : undefined;
          const ageYears = dob
            ? (calculateAgeYearsFromDateOfBirth(dob) ?? undefined)
            : undefined;
          const sexNum = selectedPatientDetails?.sex;
          const isFemale =
            sexNum === 2 ? true : sexNum === 1 ? false : undefined;
          return {
            patientId:
              selectedPatientDetails?.patientID ||
              selectedPatientConsultation?.patientId ||
              "current-patient",
            ageYears,
            dateOfBirth: dob || "",
            isFemale,
            weightKg: selectedPatientDetails?.weightKg || undefined,
            knownAllergies: (selectedPatientDetails?.allergies || []).map(
              (a) => ({
                substance:
                  typeof a === "string"
                    ? a
                    : a?.allergyName || a?.name || a?.substance || "Unknown",
                severity: a?.severity,
              }),
            ),
            currentMedications: (selectedPatientDetails?.medications || []).map(
              (m) =>
                typeof m === "string"
                  ? m
                  : m?.name || m?.medication || "Unknown",
            ),
          };
        })()}
        isDarkMode={isDarkMode}
        consultationId={consultationId}
        companyId={companyId}
        templateName="Pharmacy First - Otitis Media"
        hasExistingTemplate={hasExistingTemplate}
        templateId={effectiveTemplateId}
        restoredState={
          savedSnapshot?.engine?.pathwayId === "otitismedia"
            ? savedSnapshot.engine
            : undefined
        }
        onComplete={async (audit) => {
          setNhsAomForm(audit);
          await updateConsentObtained({
            consultationId,
            consentObtained: true,
          });
          await addupdateConsultationSummary({
            consultationId,
            summary: JSON.stringify(audit),
          });
          await clearSavedPathwayState(consultationId);
          await getPatientConsultationByConsultationId(consultationId);
          setPatientTabSwitcher(showPreConsult ? 2 : 1);
          toast({
            title: "Consultation Completed",
            description:
              "NHS Pharmacy First — Acute Otitis Media consultation has been completed successfully.",
          });
        }}
        onOpenCalculator={(calculatorId) =>
          setPendingCalculatorId(calculatorId)
        }
        onSaveVitals={handlePathwayVitalSave}
        onDemographicAmended={handleDemographicAmended}
        onConsentRefused={() => {
          updateConsentObtained({ consultationId, consentObtained: false });
        }}
        onCancel={() => {
          setNhsAomForm(null);
          onTemplateCancel?.();
        }}
      />
    )
  ) : effectiveTemplateId === nhsHypertensionStage1TemplateId ? (
    <NHSHypertensionInitialForm
      isDarkMode={isDarkMode}
      onComplete={async (data) => {
        const avgBp = calcAvgBp(data);
        setNhsHypertensionInitialFormLoading(true);
        setHypertensionInitialForm(data);
        const jsonData = JSON.stringify(data);
        await addupdateConsultationSummary({
          consultationId,
          summary: jsonData,
        });
        if (avgBp) {
          await addNewPatientVital("Blood Pressure", "bloodpressure", {
            Systolic: avgBp.systolic,
            Diastolic: avgBp.diastolic,
          });
        }
        if (data.outcome === "offer-abpm") {
          const today = new Date().toISOString().split("T")[0];
          const fittingDate = data.abpmFittingDate || today;
          const followUpDate = data.abpmFollowUpDate || today;

          const startDateTime = new Date(fittingDate);
          const now = new Date();
          startDateTime.setHours(
            now.getHours(),
            now.getMinutes(),
            now.getSeconds(),
          );
          const endDateTime = new Date(startDateTime);
          endDateTime.setMinutes(endDateTime.getMinutes() + 20);

          const followUpStartDateTime = new Date(followUpDate);
          followUpStartDateTime.setHours(
            now.getHours(),
            now.getMinutes(),
            now.getSeconds(),
          );
          const followUpEndDateTime = new Date(followUpStartDateTime);
          followUpEndDateTime.setMinutes(followUpEndDateTime.getMinutes() + 20);

          const appointmetPayload = {
            duration: 20,
            durationUnit: "m",
            resourceId: currentUser?.emailAddress,

            allDay: false,
            scheduleEventType: 1,
            serviceID: nhsHypertensionStage1ServiceId,
            patientID: selectedPatientDetails?.patientID || "",
            patientName: selectedPatientDetails?.patientName || "",
            consultationID: consultationId,
            eventStatus: 2,
          };

          const treatmentPlan = {
            treatmentPlanId: uuidv4(),
            name: `${HYPERTENSION_SERVICE_NAME} – ${selectedPatientConsultation?.patientName}`,
            description:
              "3-stage NHS hypertension case-finding pathway: Initial Consultation → ABPM Fitting → ABPM Follow Up.",
            consultationId: consultationId,
            patientId: selectedPatientConsultation?.patientId || "",
            patientName: selectedPatientConsultation?.patientName || "",
            pharmacyId: currentPharmacyId,
            createdBy: "Current User",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            startDate: today,
            endDate: followUpDate,
            status: 1,
            goals: [
              {
                id: uuidv4(),
                description: "Complete Stage 2: ABPM Fitting",
                targetValue: "Completed",
                targetDate: fittingDate,
                status: 1,
                notes: "Fit ambulatory monitor and brief the patient.",
                createdAt: new Date().toISOString(),
              },
              {
                id: uuidv4(),
                description: "Complete Stage 3: ABPM Follow Up",
                targetValue: "Completed",
                targetDate: followUpDate,
                status: 1,
                notes: "Review ABPM readings and confirm diagnosis.",
                createdAt: new Date().toISOString(),
              },
              {
                id: uuidv4(),
                description: "Daytime average BP < 135/85 mmHg",
                linkedVitalType: 2,
                targetDirection: 2,
                targetValue: "<135/85 mmHg",
                targetDate: followUpDate,
                status: 2,
                createdAt: new Date().toISOString(),
              },
            ],
            followUpAppointments: [
              {
                ...appointmetPayload,
                id: uuidv4(),
                startDate: startDateTime.toISOString(),
                endDate: endDateTime.toISOString(),
                cls: `Stage 2 – ABPM Fitting on ${fittingDate}`,
              },
              {
                ...appointmetPayload,
                id: uuidv4(),
                startDate: followUpStartDateTime.toISOString(),
                endDate: followUpEndDateTime.toISOString(),
                cls: `Stage 3 – ABPM Follow Up on ${followUpDate}`,
              },
            ],
            linkedPrescriptions: [],
          };

          const actionPayload = {
            payload: JSON.stringify(treatmentPlan),
            actionName: "CreateTreatmentPlan",
            actionNumber: "15",
            isPerformed: true,
          };
          await updateConsultationActions({
            consultationId: consultationId,
            payload: actionPayload,
          });
          try {
            await addConsultationLinkedTemplate({
              consultationId,
              templateId: selectedPatientConsultation?.templateId || "",
              templateName: selectedPatientConsultation?.templateName || "",
              patientId: selectedPatientConsultation?.patientId || "",
              patientName: selectedPatientConsultation?.patientName || "",
              pharmacyID: currentPharmacyId || "",
              createdBy: currentUser?.name || currentUser?.userName || "",
              createdAt: new Date().toISOString(),
              treatmentPlan: treatmentPlan,
            });
          } catch (err) {
            console.warn(
              "addConsultationLinkedTemplate (Hypertension) failed:",
              err,
            );
          }
          const proceedToFittingNow =
            data.outcome === "offer-abpm" &&
            data.abpmConsent === "yes" &&
            data.abpmAvailableNow === "yes";

          if (proceedToFittingNow) {
            setSelectedTemplate("nhs-htn-abpm-fitting");
            setActiveConsultationTab("template");
            toast({
              title: "Stage 1 Completed",
              description:
                "Initial consultation complete. Continuing straight to Stage 2 – ABPM Fitting.",
            });
          } else {
            await getPatientConsultationByConsultationId(consultationId);
            setPatientTabSwitcher(
              showPreConsult || showNHS111ReferralTab ? 2 : 1,
            );
            setNhsHypertensionInitialFormLoading(false);
            toast({
              title: "Stage 1 Completed",
              description:
                data.outcome === "offer-abpm"
                  ? "Initial consultation complete. Treatment Plan draft created with Stage 2 and Stage 3 follow-ups."
                  : "Initial consultation completed.",
            });
          }
        }
      }}
      nhsHypertensionInitialFormLoading={nhsHypertensionInitialFormLoading}
      consultationStatus={selectedPatientConsultation?.consultationStatus}
    />
  ) : effectiveTemplateId === nhsHypertensionABPMFittingTemplateId ? (
    <NHSHypertensionABPMFittingForm
      isDarkMode={isDarkMode}
      onComplete={async (data) => {
        setNhsHypertensionABPMFittingFormLoading(true);
        setHypertensionABPMFittingForm(data);
        const jsonData = JSON.stringify(data);
        await addupdateConsultationSummary({
          consultationId,
          summary: jsonData,
        });
        await getPatientConsultationByConsultationId(consultationId);
        setPatientTabSwitcher(showPreConsult || showNHS111ReferralTab ? 2 : 1);
        toast({
          title: "Stage 2 Completed",
          description:
            "ABPM fitting complete. Stage 3 (ABPM Follow Up) is now unlocked for this patient.",
        });
        setNhsHypertensionABPMFittingFormLoading(false);
      }}
      consultationStatus={selectedPatientConsultation?.consultationStatus}
      nhsHypertensionABPMFittingFormLoading={
        nhsHypertensionABPMFittingFormLoading
      }
    />
  ) : effectiveTemplateId === nhsHypertensionABPMFollowupTemplateId ? (
    <NHSHypertensionABPMFollowUpForm
      isDarkMode={isDarkMode}
      parseAbpmReportFromApi={parseAbpmReport}
      consultationStatus={selectedPatientConsultation?.consultationStatus}
      nhsHypertensionABPMFolloupFormLoading={
        nhsHypertensionABPMFollowupFormLoading
      }
      onComplete={async (data) => {
        setNhsHypertensionABPMFollowupFormLoading(true);
        setHypertensionABPMFollowupForm(data);

        const jsonData = JSON.stringify(data);
        await addupdateConsultationSummary({
          consultationId,
          summary: jsonData,
        });
        addNewPatientVital("Blood Pressure", "bloodpressure", {
          Systolic: data?.twentyFourHourAvgSystolic,
          Diastolic: data?.twentyFourHourAvgDiastolic,
        });
        await getPatientConsultationByConsultationId(consultationId);
        setPatientTabSwitcher(showPreConsult || showNHS111ReferralTab ? 2 : 1);

        toast({
          title: "Stage 3 Completed",
          description:
            "Hypertension case-finding pathway completed for this patient.",
        });
        setNhsHypertensionABPMFollowupFormLoading(false);
      }}
    />
  ) : effectiveTemplateId === nhsAhtWeightTemplateId ? (
    completedAudit?.pathwayId?.startsWith("aht-") ? (
      renderCompletedSummary(completedAudit.pathwayId)
    ) : (
      <PathwayEngine
        key={`${consultationId}-aht-${resolvedAhtPathwayId}`}
        pathwayId={resolvedAhtPathwayId}
        initialContextValues={{ ...ahtChainContext }}
        patient={buildPatientInput()}
        isDarkMode={isDarkMode}
        patientVitals={patientVitals}
        consultationId={consultationId}
        companyId={companyId}
        templateName="Pharmacy First - AHT Weight Management"
        hasExistingTemplate={hasExistingTemplate}
        templateId={effectiveTemplateId}
        restoredState={
          savedSnapshot?.engine?.pathwayId === resolvedAhtPathwayId
            ? savedSnapshot.engine
            : undefined
        }
        chainState={{
          ahtChainPathwayId: resolvedAhtPathwayId,
          ahtChainContext,
          ahtChainSections,
        }}
        onComplete={handleAhtComplete}
        onOpenCalculator={(calculatorId) =>
          setPendingCalculatorId(calculatorId)
        }
        onSaveVitals={handlePathwayVitalSave}
        onDemographicAmended={handleDemographicAmended}
        onConsentRefused={() => {
          updateConsentObtained({ consultationId, consentObtained: false });
        }}
        onCancel={() => {
          setNhsAhtForm(null);
          setAhtChainPathwayId("aht-wt-selector");
          setAhtChainContext({});
          setAhtChainSections([]);
          onTemplateCancel?.();
        }}
        patientDisplayName={
          selectedPatientDetails?.patientName ||
          selectedPatientConsultation?.patientName ||
          ""
        }
        patientEmail={selectedPatientDetails?.email || null}
        onUpdatePatientEmail={async (email) => {
          const { patientName, ...detailsWithoutName } = selectedPatientDetails;
          const data = { ...detailsWithoutName, email };
          const response = await updatePatientDetails(data);
          if (response === "true") {
            setSelectedPatientDetails({ ...selectedPatientDetails, email });
          }
        }}
        onCreateAHTTreatmentPlan={async (plan) => {
          const patientId =
            selectedPatientDetails?.patientID ||
            selectedPatientConsultation?.patientId ||
            "";
          const followUpEvents = (plan.plannedAppointmentDetails || []).map(
            (appointment) => ({
              id: appointment.id || uuidv4(),
              consultationID: "",
              name: appointment.serviceType || "",
              patientID: patientId,
              patientName:
                selectedPatientDetails?.patientName ||
                selectedPatientConsultation?.patientName ||
                "",
              startDate: appointment.date
                ? appointment.date + "T09:00:00Z"
                : "",
              endDate: appointment.date ? appointment.date + "T09:30:00Z" : "",
              duration: 30,
              durationUnit: "m",
              cls: "",
              recurrenceRule: "",
              exceptionDates: [],
              resourceId: currentUser?.emailAddress || "",
              allDay: false,
              scheduleEventType: 1,
              serviceID: "6880e77b98b936f8365ab340",
              email: "",
              mobile: "",
              confirmationMethod: "",
              RoomId: "",
              recordedAt: new Date().toLocaleString(),
              tasksToComplete: [],
              vaccinesAdminister: [],
              eventStatus: 2,
              pharmacyID: currentPharmacyId || "",
            }),
          );

          const statusToInt = {
            active: 1,
            completed: 2,
            paused: 3,
            cancelled: 4,
          };
          const planWithEvents = {
            ...plan,
            treatmentPlanId: plan.id,
            createdBy: currentUser?.emailAddress || "",
            pharmacyID: currentPharmacyId || "",
            status: statusToInt[String(plan.status || "").toLowerCase()] ?? 1,
            followUpAppointments: followUpEvents,
          };

          const actionPayload = {
            payload: JSON.stringify(planWithEvents),
            actionName: "CreateTreatmentPlan",
            actionNumber: "15",
            isPerformed: true,
          };
          await updateConsultationActions({
            consultationId,
            payload: actionPayload,
          });
          try {
            await addConsultationLinkedTemplate({
              consultationId,
              templateId: selectedPatientConsultation?.templateId || "",
              templateName: selectedPatientConsultation?.templateName || "",
              patientId: patientId,
              patientName:
                selectedPatientDetails?.patientName ||
                selectedPatientConsultation?.patientName ||
                "",
              pharmacyID: currentPharmacyId || "",
              createdBy: currentUser?.emailAddress,
              createdAt: new Date().toISOString(),
              treatmentPlan: planWithEvents,
            });
          } catch (err) {
            console.warn("addConsultationLinkedTemplate (AHT) failed:", err);
          }
          await getPatientConsultationByConsultationId(consultationId);
          const resolvedPatientId =
            selectedPatientDetails?.patientID ||
            selectedPatientConsultation?.patientId ||
            "";
          if (resolvedPatientId) {
            await getPatientActiveTreatmentPlan(resolvedPatientId);
          }
          toast({
            title: "Treatment Plan Created",
            description:
              "AHT Weight Management treatment plan has been created successfully.",
          });
        }}
      />
    )
  ) : effectiveTemplateId === nhsAhtFollowUpTemplateId ? (
    (() => {
      const plan = getAHTTreatmentPlan();
      const dob =
        selectedPatientDetails?.dateOfBirth ||
        selectedPatientDetails?.dob ||
        "";
      return (
        <AHTFollowUpReviewTemplate
          isDarkMode={isDarkMode}
          patientName={
            selectedPatientDetails?.patientName ||
            selectedPatientConsultation?.patientName ||
            ""
          }
          patientDob={dob}
          drug={plan?.resolvedDrugId}
          currentDose={plan?.currentDose}
          treatmentWeek={plan?.currentWeek}
          cycleNumber={plan?.maintenanceEpisodeCount}
          presciberName={currentUser?.name || currentUser?.userName || ""}
          presciberRegNumber={currentUser?.registrationNumber || ""}
          onComplete={handleAhtFollowUpComplete}
          loading={ahtFollowUpLoading}
        />
      );
    })()
  ) : effectiveTemplateId === nhsAhtSixMonthTemplateId ? (
    (() => {
      const plan = getAHTTreatmentPlan();
      const dob =
        selectedPatientDetails?.dateOfBirth ||
        selectedPatientDetails?.dob ||
        "";
      const latestWeight = plan?.supplyHistory?.length
        ? plan.supplyHistory[plan.supplyHistory.length - 1]?.weightKg
        : undefined;
      return (
        <AHTSixMonthReviewTemplate
          isDarkMode={isDarkMode}
          patientName={
            selectedPatientDetails?.patientName ||
            selectedPatientConsultation?.patientName ||
            ""
          }
          patientDob={dob}
          drug={plan?.resolvedDrugId}
          currentDose={plan?.currentDose}
          baselineWeightKg={plan?.baselineWeightKg}
          baselineBmi={plan?.baselineBmi}
          currentWeightKg={latestWeight}
          presciberName={currentUser?.name || currentUser?.userName || ""}
          presciberRegNumber={currentUser?.registrationNumber || ""}
          initialSupplyDate={plan?.initialSupplyDate}
          onComplete={handleAhtSixMonthComplete}
          loading={ahtSixMonthLoading}
        />
      );
    })()
  ) : (
    <NHSContraceptionForm
      isDarkMode={isDarkMode}
      onComplete={async (data) => {
        setNhsContraceptionFormLoading(true);
        setNhsContraceptionForm(data);
        const jsonData = JSON.stringify(data);
        await addupdateConsultationSummary({
          consultationId,
          summary: jsonData,
        });
        if (
          data.serviceType === "oral-contraception" &&
          data.ocPillType === "combined"
        ) {
          if (data.vitalsBpSystolic && data.vitalsBpDiastolic) {
            await addNewPatientVital("Blood Pressure", "bloodpressure", {
              Systolic: Number(data.vitalsBpSystolic),
              Diastolic: Number(data.vitalsBpDiastolic),
            });
          }

          if (data.vitalsWeight) {
            await addNewPatientVital("Weight", "weight", {
              Value: Number(data.vitalsWeight),
              Unit: data.vitalsWeightUnit || "kg",
            });
          }

          if (data.vitalsHeight || data.vitalsHeightFeet) {
            const heightVal =
              data.vitalsHeightUnit === "ft/in"
                ? {
                    Feet: Number(data.vitalsHeightFeet || 0),
                    Inch: Number(data.vitalsHeightInches || 0),
                  }
                : Number(data.vitalsHeight);

            await addNewPatientVital("Height", "height", {
              Value: heightVal,
              Unit: data.vitalsHeightUnit === "ft/in" ? "ft/in" : "cm",
            });
          }
          await getPatientConsultationByConsultationId(consultationId);
        }

        setPatientTabSwitcher(showPreConsult || showNHS111ReferralTab ? 2 : 1);

        toast({
          title: "Consultation Completed",
          description:
            "NHS Contraception consultation has been completed successfully.",
        });
        setNhsContraceptionFormLoading(false);
      }}
      consultationStatus={selectedPatientConsultation?.consultationStatus}
      nhsContraceptionFormLoading={nhsContraceptionFormLoading}
    />
  );
}
